import { getAccessToken, getUserId } from "../../services/cookieService";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../Store";
import cartService from "services/cartService";
import { CartUpdateActionType } from "data/cartActions";
import {
  ICartItem,
  ICartItemResponse,
  ICartState,
} from "store/types/cartTypes";
import { IAttributeImage } from "Interfaces/interface.productsapi";
import { ITEM_TYPES } from "utils/itemTypeHelpers";
import userAlertStatusService from "services/dataRemovalNotifyService";
import { UpdatedRemovalFeature } from "data/removalNotice";
import { extractErrorMessage } from "utils/ErrorHandlers";
import {
  fetchCartConfigurationsAsync,
  fetchConfigurationForNewCartItemAsync,
  selectIsCartConfigurationsLoaded,
} from "./configurationSlice";

const extractNPINsFromCartItems = (items: ICartItem[]): string[] => {
  return items
    .map((item) => item.identification?.NPIN)
    .filter((npin): npin is string => Boolean(npin));
};

const getInitialItems = (): ICartItem[] => {
  const items = JSON.parse(localStorage.getItem("cart") || "[]");
  return items.map((item: any) => ({
    ...item,
    id: item.id || item.productId,
  }));
};

const initialState: ICartState = {
  items: getInitialItems(),
  status: "idle",
  error: null,
  order: null,
  isCartLoaded: false,
  isCartLoading: false,
  isDataRemoved: false,
  hasInitialFetchStarted: false,
};

export const fetchCart = createAsyncThunk<
  ICartItemResponse[],
  { userId: string; isAlertRequired?: boolean },
  { rejectValue: string }
>(
  "cart/fetchCart",
  async (
    { userId, isAlertRequired = true },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const order = await cartService.getCartByUser(userId, isAlertRequired);

      const state = getState() as RootState;
      const isConfigurationsLoaded = selectIsCartConfigurationsLoaded(state);

      if (order && order.length > 0 && !isConfigurationsLoaded) {
        const cartItems = order.map((line: any) => ({
          id: line.id,
          productId: line.id,
          variantId: line.variants?.[0]?.id || "",
          quantity: line.qty || 25,
          identification: line.identification,
          variants: line.variants || [],
          attributes: line.attributes || [],
          itemType: line.itemType || ITEM_TYPES.PRODUCTS, // Map from backend
        }));

        const npins = extractNPINsFromCartItems(cartItems);

        if (npins.length > 0) {
          dispatch(fetchCartConfigurationsAsync({ npins, cartItems }));
        }
      }

      return order;
    } catch (error: any) {
      return rejectWithValue(
        extractErrorMessage(error) || "Failed to fetch cart"
      );
    }
  }
);

export const updateCart = createAsyncThunk<
  ICartItemResponse[],
  {
    userId: string;
    productId: string;
    productGroupId?: string;
    variantId: string;
    quantity: number;
    actionType: string;
    itemType?: typeof ITEM_TYPES[keyof typeof ITEM_TYPES];
  },

  { rejectValue: string }
>("cart/updateCart", async (payload, { rejectWithValue }) => {
  try {
    const { userId, productId, productGroupId, variantId, quantity, actionType, itemType } = payload;

    // Import mapper at runtime to avoid circular dependency
    const { mapItemTypeToBackend } = await import('utils/itemTypeHelpers');
    const backendItemType = mapItemTypeToBackend(itemType);

    const order = await cartService.updateCart(
      actionType,
      userId,
      productId,
      variantId,
      quantity,
      backendItemType,
      productGroupId,
    );

    return order;
  } catch (error: any) {
    console.error("Update cart error:", error);
    return rejectWithValue(
      extractErrorMessage(error) || "Failed to update cart"
    );
  }
});

export const addToCart = createAsyncThunk<
  ICartItem,
  ICartItem,
  { state: RootState; rejectValue: string }
>("cart/addToCart", async (item, { dispatch, rejectWithValue, getState }) => {
  const NGAT = getAccessToken();
  const userId = getUserId();

  // Ensure itemType is set and id is present (especially for guest users)
  const itemWithType: ICartItem = {
    ...item,
    id: item.id || item.productId,
    itemType: item.itemType || ITEM_TYPES.PRODUCTS,
  };


  if (NGAT && userId) {
    try {
      const payload = {
        userId,
        productId: itemWithType.productId,
        variantId: itemWithType.variantId,
        quantity: itemWithType.quantity,
        actionType: CartUpdateActionType.ADD,
        itemType: itemWithType.itemType,
        productGroupId: itemWithType.productId,
      };

      await dispatch(updateCart(payload)).unwrap();

      const newNPIN = itemWithType.identification?.NPIN;
      if (newNPIN) {
        const state = getState() as RootState;
        const currentCartItems = [...state.cart.items, itemWithType];

        dispatch(
          fetchConfigurationForNewCartItemAsync({
            newNPIN,
            allCartItems: currentCartItems,
          })
        );
      }

      return itemWithType;
    } catch (error: any) {
      console.error("Add to collection error:", error);
      return rejectWithValue(
        extractErrorMessage(error) || "Failed to add item to collection"
      );
    }
  }

  // Guest user: return item with itemType
  return itemWithType;
});

export const bulkAddToCart = createAsyncThunk<
  ICartItem[],
  ICartItem[],
  { state: RootState; rejectValue: string }
>("cart/bulkAddToCart", async (items, { rejectWithValue }) => {
  const NGAT = getAccessToken();

  if (!NGAT) {
    return rejectWithValue("User not authenticated");
  }

  try {
    // Ensure all items have itemType field (default to 'product')
    const itemsWithType = items.map(item => ({
      ...item,
      itemType: item.itemType || ITEM_TYPES.PRODUCTS,
    }));


    // Import mapper at runtime to avoid circular dependency
    const { mapItemTypeToBackend } = await import('utils/itemTypeHelpers');

    const payload = itemsWithType.map((item) => ({
      productId: item.productId,
      productGroupId: item.itemType === ITEM_TYPES.PRODUCT_GROUP ? item.id : undefined,
      variantId: item.variantId || "",
      quantity: item.quantity,
      itemType: mapItemTypeToBackend(item.itemType) as typeof ITEM_TYPES[keyof typeof ITEM_TYPES],
    }));

    await cartService.bulkAddToCart(payload);
    return itemsWithType;
  } catch (error: any) {
    console.error("Bulk add to collection error:", error);
    return rejectWithValue(error.message || "Failed to bulk add items to cart");
  }
});

export const removeItemAsync = createAsyncThunk<
  { productId: string; variantId: string },
  { productId: string; variantId: string; productGroupId?: string },
  { state: RootState; rejectValue: string }
>(
  "cart/removeItemAsync",
  async ({ productId, variantId, productGroupId }, { dispatch, rejectWithValue, getState }) => {
    const NGAT = getAccessToken();
    const userId = getUserId();

    if (NGAT && userId) {
      try {
        // Get itemType from cart state
        const state = getState();
        const cartItem = state.cart.items.find(
          item => item.id === productId && item.variantId === variantId
        );
        const itemType = cartItem?.itemType || ITEM_TYPES.PRODUCTS;

        const payload = {
          userId,
          productId,
          variantId,
          quantity: 0,
          actionType: CartUpdateActionType.REMOVE,
          itemType,
          productGroupId: productGroupId || (itemType === ITEM_TYPES.PRODUCT_GROUP ? productId : undefined),
        };

        await dispatch(updateCart(payload)).unwrap();
        return { productId, variantId };
      } catch (error: any) {
        console.error("Remove item error:", error);
        return rejectWithValue(
          extractErrorMessage(error) || "Failed to remove item from cart"
        );
      }
    }

    // For guest users, return the IDs to trigger local state update
    return { productId, variantId };
  }
);

export const clearCartAsync = createAsyncThunk<
  void,
  string,
  { state: RootState; rejectValue: string }
>("cart/clearCartAsync", async (actionType, { dispatch, rejectWithValue }) => {
  const NGAT = getAccessToken();
  const userId = getUserId();

  if (NGAT && userId) {
    try {
      await cartService.clearCart(actionType);
      return;
    } catch (error: any) {
      console.error("Clear cart error:", error);
      return rejectWithValue(
        extractErrorMessage(error) || "Failed to clear cart"
      );
    }
  }

  return;
});

export const resolveCartDataRemoved = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>("cart/resolveCartDataRemoved", async (_, { rejectWithValue, dispatch }) => {
  try {
    await userAlertStatusService.resolveDataRemoved({
      dataType: UpdatedRemovalFeature.CART,
    });
  } catch (error: any) {
    console.error("Resolve CART dataRemoved error:", error);
    return rejectWithValue(
      extractErrorMessage(error) || "Failed to resolve CART flag"
    );
  }
});

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addItem: (state, action: PayloadAction<ICartItem>) => {
      if (getAccessToken() && getUserId()) {
        return; // No local updates for logged-in users
      }
      const existing = state.items.find(
        (item) =>
          item.productId === action.payload.productId &&
          item.variantId === action.payload.variantId
      );
      if (existing) {
        existing.quantity += action.payload.quantity;
      } else {
        state.items.unshift(action.payload);
      }
      localStorage.setItem("cart", JSON.stringify(state.items));
    },
    removeItem: (
      state,
      action: PayloadAction<{ productId: string; variantId: string }>
    ) => {
      if (getAccessToken() && getUserId()) {
        return; // No local updates for logged-in users
      }
      state.items = state.items.filter(
        (item) =>
          !((item.id === action.payload.productId || item.productId === action.payload.productId) &&
            item.variantId === action.payload.variantId)
      );
      localStorage.setItem("cart", JSON.stringify(state.items));
    },
    updateQuantity: (
      state,
      action: PayloadAction<{
        productId: string;
        variantId: string;
        quantity: number;
        actionType?: string;
      }>
    ) => {
      if (getAccessToken() && getUserId()) {
        return; // No local updates for logged-in users
      }
      const item = state.items.find(
        (i) =>
          (i.id === action.payload.productId || i.productId === action.payload.productId) &&
          i.variantId === action.payload.variantId
      );
      if (item) {
        item.quantity = action.payload.quantity;
        localStorage.setItem("cart", JSON.stringify(state.items));
      }
    },
    clearCart: (state) => {
      if (getAccessToken() && getUserId()) {
        return;
      }
      state.items = [];
      localStorage.removeItem("cart");
      state.order = null;
      state.isCartLoaded = false;
      state.isCartLoading = false;
      state.status = "idle";
      state.error = null;
    },
    incrementQuantity: (
      state,
      action: PayloadAction<{ productId: string; variantId: string }>
    ) => {
      if (getAccessToken() && getUserId()) {
        return; // No local updates for logged-in users
      }
      const item = state.items.find(
        (i) =>
          (i.id === action.payload.productId || i.productId === action.payload.productId) &&
          i.variantId === action.payload.variantId
      );
      if (item) {
        item.quantity += 1;
        localStorage.setItem("cart", JSON.stringify(state.items));
      }
    },
    decrementQuantity: (
      state,
      action: PayloadAction<{ productId: string; variantId: string }>
    ) => {
      if (getAccessToken() && getUserId()) {
        return; // No local updates for logged-in users
      }
      const item = state.items.find(
        (i) =>
          (i.id === action.payload.productId || i.productId === action.payload.productId) &&
          i.variantId === action.payload.variantId
      );
      if (item && item.quantity > 25) {
        item.quantity -= 1;
        localStorage.setItem("cart", JSON.stringify(state.items));
      }
    },
    setCartDataRemoved: (state, action: PayloadAction<boolean>) => {
      state.isDataRemoved = action.payload;
    },
    removeInvalidItems: (state, action: PayloadAction<ICartItem[]>) => {
      state.items = state.items.filter(
        (item) =>
          !action.payload.some(
            (invalid) =>
              invalid.id === item.id &&
              invalid.variantId === item.variantId
          )
      );
      if (!(getAccessToken() && getUserId())) {
        localStorage.setItem("cart", JSON.stringify(state.items));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => {
        state.status = "loading";
        state.isCartLoading = true;
        state.hasInitialFetchStarted = true;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isCartLoading = false;
        state.hasInitialFetchStarted = true;
        state.items = action.payload.map((line) => {
          // Import mapper to convert backend itemType to frontend format
          const isKit = line.itemType === ITEM_TYPES.PRODUCT_GROUP;


          // For products: use variants[0], for kits: use line-level price
          const variant = !isKit && line.variants?.[0] ? line.variants[0] : {
            id: "",
            price: line.price || "0",
            variantId: "",
            attributes: {},
          };

          const sanitizedImages: (IAttributeImage | string)[] = (line.images || []).map((img: any) => {
            if (typeof img === 'string') return img;
            return {
              ...img,
              attributeValues: Object.entries(img.attributeValues || {}).reduce(
                (acc, [key, value]) => {
                  if (value !== undefined) acc[key] = value as string;
                  return acc;
                },
                {} as { [key: string]: string }
              ),
            };
          });

          return {
            id: line.id,
            productId: line.id,
            variantId: variant.id,
            quantity: line.qty || 25,
            price: parseFloat(variant.price) || parseFloat(line.price) || 0,
            name: line.name || "Unknown Product",
            images: sanitizedImages,
            attributes: line.attributes || [],
            category: line.category?.name || line.categoryName || "",
            brand: line.brand?.name || line.brandName || "",
            categoryId: line.categoryId || "",
            brandId: line.brandId || "",
            description: line.description || "",
            variants: line.variants || [],
            identification: line.identification,
            itemType: isKit ? ITEM_TYPES.PRODUCT_GROUP : ITEM_TYPES.PRODUCTS,
            product_group_items: line.product_group_items || [],
            productsCount: line.productsCount || (line.product_group_items?.length || 0),
          };

        });
        if (!(getAccessToken() && getUserId())) {
          localStorage.setItem("cart", JSON.stringify(state.items));
        }
        state.isCartLoaded = true;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.status = "failed";
        state.isCartLoading = false;
        state.hasInitialFetchStarted = true;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : action.error?.message || "Failed to fetch cart";
      })
      .addCase(updateCart.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateCart.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isCartLoaded = false;
        if (!(getAccessToken() && getUserId())) {
          localStorage.setItem("cart", JSON.stringify(state.items));
        }
        state.isCartLoaded = false;
      })
      .addCase(updateCart.rejected, (state, action) => {
        state.status = "failed";

        state.error = action.payload ?? "Failed to update cart";
        state.isCartLoaded = false;
      })
      .addCase(addToCart.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (!(getAccessToken() && getUserId())) {
          const existing = state.items.find(
            (item) =>
              item.id === action.payload.productId &&
              item.variantId === action.payload.variantId
          );
          if (existing) {
            existing.quantity += action.payload.quantity;
          } else {
            state.items.unshift(action.payload);
          }
          localStorage.setItem("cart", JSON.stringify(state.items));
        }
        state.isCartLoaded = false;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? "Add to collection failed.";
      })
      .addCase(removeItemAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(removeItemAsync.fulfilled, (state, action) => {
        state.status = "succeeded";

        if (!(getAccessToken() && getUserId())) {
          // Guest user: remove from state and localStorage
          state.items = state.items.filter(
            (item) =>
              !((item.id === action.payload.productId || item.productId === action.payload.productId) &&
                item.variantId === action.payload.variantId)
          );
          localStorage.setItem("cart", JSON.stringify(state.items));
        }
        state.isCartLoaded = false;
      })
      .addCase(removeItemAsync.rejected, (state, action) => {
        state.status = "failed";

        state.error = action.payload ?? "Failed to remove item";
      })
      .addCase(clearCartAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(clearCartAsync.fulfilled, (state) => {
        state.status = "succeeded";
        state.items = [];
        state.order = null;
        if (!(getAccessToken() && getUserId())) {
          localStorage.removeItem("cart");
        }
        state.isCartLoaded = false;
      })
      .addCase(clearCartAsync.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? "Failed to clear cart";
      })
      .addCase(resolveCartDataRemoved.pending, (state) => {
        state.status = "loading";
      })
      .addCase(resolveCartDataRemoved.fulfilled, (state) => {
        state.status = "succeeded";
        state.isDataRemoved = false;
        state.isCartLoaded = false;
      })
      .addCase(resolveCartDataRemoved.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? "Failed to resolve cart data removal";
      });
  },
});

export const {
  addItem,
  removeItem,
  updateQuantity,
  clearCart,
  incrementQuantity,
  decrementQuantity,
  setCartDataRemoved,
  removeInvalidItems,
} = cartSlice.actions;

export const updateCartQuantity =
  (
    productId: string,
    variantId: string,
    quantity: number,
    actionType: string
  ) =>
    (dispatch: any, getState: () => RootState) => {
      const NGAT = getAccessToken();
      const userId = getUserId();

      if (NGAT && userId) {
        // Get itemType from cart state
        const state = getState();
        const cartItem = state.cart.items.find(
          item => item.id === productId && item.variantId === variantId
        );
        const itemType = cartItem?.itemType || ITEM_TYPES.PRODUCTS;

        const payload = {
          userId,
          productId,
          productGroupId: itemType === ITEM_TYPES.PRODUCT_GROUP ? productId : undefined,
          variantId,
          quantity,
          actionType,
          itemType,
        };
        return dispatch(updateCart(payload));
      } else {
        if (actionType === CartUpdateActionType.INCREMENT) {
          dispatch(incrementQuantity({ productId, variantId }));
        } else if (actionType === CartUpdateActionType.DECREMENT) {
          dispatch(decrementQuantity({ productId, variantId }));
        } else if (actionType === CartUpdateActionType.CUSTOM_QUANTITY) {
          dispatch(updateQuantity({ productId, variantId, quantity }));
        }
        // Return a dummy result that matches the structure expected by the caller
        return { meta: { requestStatus: "fulfilled" } };
      }
    };

export default cartSlice.reducer;

export const selectCartItems = (state: RootState) => state.cart.items;
export const selectCartCount = (state: RootState) =>
  state.cart.items.reduce((count, item) => count + item.quantity, 0);
export const selectCartStatus = (state: RootState) => state.cart.status;
export const selectCartError = (state: RootState) => state.cart.error;
export const selectCartOrder = (state: RootState) => state.cart.order;
export const selectCartTotal = (state: RootState) =>
  state.cart.items.reduce(
    (total, item) => total + (item.price ?? 0) * item.quantity,
    0
  );

export const selectHasInitialFetchStarted = (state: RootState) =>
  state.cart.hasInitialFetchStarted;
export const selectIsCartLoaded = (state: RootState) => state.cart.isCartLoaded;
export const selectIsCartLoading = (state: RootState) =>
  state.cart.isCartLoading;
export const selectIsCartDataRemoved = (state: RootState) =>
  state.cart.isDataRemoved;
